/**
 * Statistics Reveal Section JavaScript
 *
 * Adapts the circle-fill technique from section-statistics.js (stroke-dasharray/
 * stroke-dashoffset driven by data-percentage, re-triggered via IntersectionObserver)
 * for a per-row rather than per-section trigger: each stat row gets its own
 * observer entry, so it animates independently the moment IT scrolls into view.
 * The headline wipe and subhead fade are pure CSS, driven off the same
 * "is-revealed" class this script toggles per row.
 */

(function () {
  var CIRCLE_RADIUS = 45; // matches the r="45" on the SVG circles in the section markup
  var CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

  function setCircleProgress(row, animate) {
    var circle = row.querySelector(".stat-reveal-circle-progress");
    if (!circle) return;

    var percentage = parseFloat(row.getAttribute("data-percentage")) || 0;
    percentage = Math.min(Math.max(percentage, 0), 100);
    var offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;

    circle.style.strokeDasharray = CIRCUMFERENCE;

    if (!animate) {
      // Animation disabled: jump straight to the final value, no transition.
      circle.style.transition = "none";
      circle.style.strokeDashoffset = offset;
      return;
    }

    // Start from empty, then let the CSS transition (set in
    // section-statistics-reveal.css) animate to the real offset.
    circle.style.strokeDashoffset = CIRCUMFERENCE;
    // Force a reflow so the browser registers the "empty" state before the
    // next value is applied — otherwise both writes can collapse into a
    // single paint and the transition never runs.
    void circle.getBoundingClientRect();

    requestAnimationFrame(function () {
      circle.style.strokeDashoffset = offset;
    });
  }

  function revealRow(row) {
    if (row.classList.contains("is-revealed")) return;
    row.classList.add("is-revealed");
    setCircleProgress(row, true);
  }

  function showFinalStateImmediately(row) {
    row.classList.add("is-revealed");
    setCircleProgress(row, false);
  }

  function primeRow(row) {
    var circle = row.querySelector(".stat-reveal-circle-progress");
    if (circle) {
      circle.style.strokeDasharray = CIRCUMFERENCE;
      circle.style.strokeDashoffset = CIRCUMFERENCE;
    }
  }

  function initStatRevealSection(section) {
    if (section.dataset.statRevealInitialized === "true") return;
    section.dataset.statRevealInitialized = "true";

    var rows = Array.prototype.slice.call(section.querySelectorAll("[data-stat-reveal-row]"));
    if (!rows.length) return;

    var animationEnabled = section.dataset.animation !== "false";

    if (!animationEnabled) {
      rows.forEach(showFinalStateImmediately);
      return;
    }

    rows.forEach(primeRow);

    if (!("IntersectionObserver" in window)) {
      // No observer support: just reveal everything up front rather than
      // leaving stats permanently hidden.
      rows.forEach(revealRow);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            revealRow(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
    );

    rows.forEach(function (row) {
      observer.observe(row);
    });
  }

  function initAll() {
    document.querySelectorAll(".stat-reveal-section").forEach(initStatRevealSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Theme editor: a newly added/re-rendered section arrives after
  // DOMContentLoaded has already fired, so it needs its own init pass.
  document.addEventListener("shopify:section:load", function (event) {
    var section = event.target.querySelector
      ? event.target.querySelector(".stat-reveal-section")
      : null;
    if (section) initStatRevealSection(section);
  });
})();
